/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { useQuery, useMutation, useQueries } from 'react-query'
import { useMemo } from 'react'

import type { QueryHook } from '@modules/query/types'
import {
  listTrafficFilters,
  createTrafficFilter,
  patchTrafficFilter,
  deleteTrafficFilter,
  getTrafficFilter,
} from '@modules/network-user-api/v1/callers'
import type {
  CreateTrafficFilterRequest,
  TrafficFilterInfo,
  PatchTrafficFilterRequest,
} from '@modules/network-user-api/v1/types'
import { queryClient } from '@modules/query'
import {
  useListElasticsearchProjectsQuery,
  useListObservabilityProjectsQuery,
  useListSecurityProjectsQuery,
} from '@modules/project-user-lib/hooks/list'
import usePermissions from '@modules/permissions-lib/hooks'

export const useListServerlessTrafficFiltersQuery: QueryHook<typeof listTrafficFilters> = () =>
  useQuery('listServerlessTrafficFilters', () => listTrafficFilters())

export const useGetServerlessTrafficFilterQuery = ({ ids }: { ids: string[] }) =>
  useQueries(
    ids.map((id) => ({
      queryKey: ['getServerlessTrafficFilter', id],
      queryFn: () => getTrafficFilter({ pathParameters: { id } }),
      enabled: ids.length > 0,
    })),
  )

export const useCreateServerlessTrafficFilterMutation = () =>
  useMutation<TrafficFilterInfo, unknown, { body: CreateTrafficFilterRequest }>({
    mutationFn: ({ body }) => createTrafficFilter({ body }),
    onSuccess: () => queryClient.invalidateQueries('listServerlessTrafficFilters'),
  })

export const useUpdateServerlessTrafficFilterMutation = () =>
  useMutation<TrafficFilterInfo, unknown, { id: string; body: PatchTrafficFilterRequest }>({
    mutationFn: ({ id, body }) => patchTrafficFilter({ pathParameters: { id }, body }),
    onSuccess: () => queryClient.invalidateQueries('listServerlessTrafficFilters'),
  })

export const useDeleteServerlessTrafficFilterMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationFn: ({ id }) =>
      deleteTrafficFilter({
        pathParameters: { id },
      }),
    onSuccess: () => queryClient.invalidateQueries('listServerlessTrafficFilters'),
  })

export const useListAssociableProjects = (
  { enabled = true }: { enabled?: boolean } = { enabled: true },
) => {
  const hasListElasticsearchPermission = usePermissions([
    { type: 'project-elasticsearch', action: 'list' },
  ]).hasPermissions
  const hasListObservabilityPermission = usePermissions([
    { type: 'project-observability', action: 'list' },
  ]).hasPermissions
  const hasListSecurityPermission = usePermissions([
    { type: 'project-security', action: 'list' },
  ]).hasPermissions

  const { data: elasticsearchProjects, isLoading: isElasticsearchProjectsLoading } =
    useListElasticsearchProjectsQuery({
      enabled: enabled && hasListElasticsearchPermission,
    })
  const { data: observabilityProjects, isLoading: isObservabilityProjectsLoading } =
    useListObservabilityProjectsQuery({
      enabled: enabled && hasListObservabilityPermission,
    })
  const { data: securityProjects, isLoading: isSecurityProjectsLoading } =
    useListSecurityProjectsQuery({
      enabled: enabled && hasListSecurityPermission,
    })

  const projects = useMemo(
    () => [
      ...(elasticsearchProjects?.items || []),
      ...(observabilityProjects?.items || []),
      ...(securityProjects?.items || []),
    ],
    [elasticsearchProjects, observabilityProjects, securityProjects],
  )

  const isLoading =
    isElasticsearchProjectsLoading || isObservabilityProjectsLoading || isSecurityProjectsLoading

  return {
    projects,
    isReady: !isLoading,
  }
}
